<template>
  <Dialog
    :visible="show"
    :modal="true"
    class="end-session-modal"
    closeOnEscape
    :closable="false"
    :class="step === 1 ? 'smaller' : ''"
    :header="false"
    :close-on-escape="true"
    v-focustrap
  >
    <IconButton
      :icon="CloseGreyIcon"
      class="close-button"
      alt="Close menu"
      :onclick="
        () => {
          this.hide();
        }
      "
      xsmall
      :enterPress="        
        () => {
          this.hide();
        }"
      ariaLabel="Close menu"
    />

    <header class="end-session-header">
      <h1>{{ step === 1 ? 'End session?' : step === 2 ? 'Session ended' : '' }}</h1>
    </header>

    <p class="end-session content" v-if="step === 1">
      Clicking end will save the transcript and audio you just created. You can
      still use this session for more captioning, just click start again.
    </p>
    <div class="session-ended content" v-else-if="step === 2">
      <div class="session-name-field">
        <label for="sessionName">Session name</label>
        <InputText
          id="sessionName"
          v-model="sessionTitleInput"
          aria-describedby="session-name"
          size="large"
        />
        <div class="filename-disclaimer" v-if="!validFileName">
          <i class="pi pi-exclamation-circle" style="font-size: 2rem"></i>
          <span>Please use only letters, numbers, underscores, or hyphens</span>
        </div>
      </div>
      <div class="session-ended-body">
        The transcript and audio from this session have been saved in Messenger
        Pigeon. <b>Do you want to save a separate copy as well?</b>
      </div>
    </div>

    <template #footer>
      <template v-if="step === 1">
        <div class="end-session-buttons">
          <RoundedButton
            autofocus="true"
            text="Cancel"
            ariaLabel="Cancel"
            fitHeight
            lessRound
            grey
            extrawide
            @click="hide"
            :enterPress="hide"
          />
          <RoundedButton
            :text="isProUser ? 'End and Save' : 'End and Submit'"
            :ariaLabel="isProUser ? 'End and Save' : 'End and Submit'"
            fitHeight
            lessRound
            accent
            extrawide
            @click="endSession"
            :enterPress="endSession"
          />
        </div>
      </template>
      <template v-else-if="step === 2">
        <div class="session-ended-buttons">
          <RoundedButton
            text="Save copy of transcript"
            :ariaLabel="'Save copy of transcript'"
            lessRound
            grey
            extrawide
            morePadding
            @click="downloadFiles"
            :enterPress="downloadFiles"
          />
          <!-- <span>
            Removed for patch v1.3.1  until design specification is finalized for audio downloads`
            <RoundedButton
              :style="'width:100%'"
              :text="saveAudioButtonText"
              @click="saveAudioLocally()"
              lessRound
              grey
              extrawide
              :loading="loading"
              :disabled="saveAudioButtonDisabled"
            />
          </span> -->
          <RoundedButton 
            text="Exit" 
            ariaLabel="Exit"
            lessRound 
            extrawide 
            @click="exit" 
            :enterPress="exit"
            accent 
          />
        </div>
      </template>
    </template>

  </Dialog>
</template>

<script>
import RoundedButton from "./RoundedButton";
import DownloadWhiteIcon from "../assets/images/Download-white.svg";
import PDFFileGreyIcon from "../assets/images/PDF-file-grey.svg";
import AudioFileGreyIcon from "../assets/images/Audio-file-grey.svg";
import IconButton from "../components/IconButton";
import CloseGreyIcon from "../assets/images/Close-grey.svg";
import CheckmarkCircleTurquoiseIcon from "../assets/images/Checkmark-circle-turquoise.svg";
import { getCurrentUserSubscriptionDetails } from "../assets/helpers_legacy";
import { downloadFile } from "../assets/downloadFile.js";

export default {
  components: {
    RoundedButton,
    IconButton,
  },

  props: {
    hideModal: Function,
    showDialog: Boolean,
  },

  computed: {
    fileNameWithoutFormat() {
      return this.$store.state.user.sessionDetails.title + " transcript.";
    },

    fileNamePDF() {
      return this.fileNameWithoutFormat + "pdf";
    },

    fileNameDOCX() {
      return this.fileNameWithoutFormat + "docx";
    },

    fileNameRTF() {
      return this.fileNameWithoutFormat + "rtf";
    },

    validFileName() {
      return this.FILENAME_REGEX.test(this.$store.state.user.sessionDetails.title);
    },
  },

  watch: {
    showDialog: function (newVal) {
      this.show = newVal;
    },

    show: function (newVal) {
      this.$emit("updateShow", newVal);
      if (newVal === false) {
        this.step = 1;
      }
    },
    // temp fix to save changed file name to download
    sessionTitleInput() {
      this.$store.state.user.sessionDetails.title = this.sessionTitleInput;
    },
  },

  data() {
    return {
      DownloadWhiteIcon,
      PDFFileGreyIcon,
      AudioFileGreyIcon,
      CheckmarkCircleTurquoiseIcon,
      CloseGreyIcon,

      ENDPOINT: "https://mp-server.habitatlearn.com",
      FILENAME_REGEX: /^[\w,\s-]+$/,
      show: false,
      step: 1,
      sessionTitleInput: this.$store.state.user.sessionDetails.title.replace(
        /\/|:/g,
        "-"
      ),
      shouldKeepAudioFile: true,
      shouldDownloadTranscript: false,
      shouldDownloadPDF: false,
      shouldDownloadDOCX: true,
      shouldDownloadRTF: false,
      sendLink: false,
      loading: false,
      saveAudioButtonText: "Save audio to computer",
      saveAudioButtonDisabled: true,

      currentUserSubscriptionDetails: {},
    };
  },

  async created() {
    this.currentUserSubscriptionDetails =
      await getCurrentUserSubscriptionDetails();
  },

  methods: {
    saveAudioLocally() {
      this.loading = true;
      this.$emit("saveAudioLocally");
    },
    nextStep() {
      this.step++;
      if (this.step > 2) {
        this.hide();
      }
    },

    endSession() {
      this.$emit("endSTTSession");
      this.nextStep();
    },

    hide() {
      this.show = false;
    },

    handleKeepOrDeleteAudioFile() {
      if (!this.shouldKeepAudioFile) {
        // Delete the audio file
      }

      this.sendFileLinks();
      this.nextStep();
    },

    sendFileLinks() {
      // Send the email containing links to download files
    },

    exit() {
      this.$router.push("/home");
    },

    saveToComputer() {
      const docHTML = this.$store.state.user.editor.getHTML();
      let typeSuffix = "InstantSession";
      downloadFile(docHTML, this.$store.state.user.sessionDetails.title, typeSuffix);
    },
  },
};
</script>

<style lang="scss">
.end-session-modal {
  width: 55rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 1.4rem;
  overflow: hidden;
  &.p-dialog {
    background: map-get($colours, white);
  }

  .close-button {
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
  }

  .end-session-header h1 {
    margin: 0;
  }

  .p-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 4rem;

    .content {
      text-align: center;
      padding: 3.2rem 0rem 0.8rem;
      margin: 0;
      width: 100%;
      color: map-get($colours, black);
    }

    .end-session {
      max-width: 44rem;
    }

    .session-ended.content {
      .session-name-field {
        display: flex;
        flex-direction: column;
        width: 100%;
        --p-inputtext-background: map-get($colours, white);
        --p-inputtext-color: map-get($colours, black);

        label {
          @include section-sub-header;
          color: map-get($colours, grey-4);
          text-align: left;
          text-transform: uppercase;
          margin-bottom: 0.8rem;
        }
      }

      .session-ended-body {
        text-align: left;
        margin-top: 1.6rem;
      }

      .toggle-text {
        text-align: left;
        margin-top: 2.4rem;

        p {
          @include callout;
          color: map-get($colours, grey-6);
          margin: 0.8rem 0;
        }
      }

      .keep-audio-file-field {
        display: flex;
        align-items: center;
        margin-top: 3.2rem;

        label {
          @include callout;
          margin-right: 1.2rem;

          color: map-get($colours, accent-purple-2);
          .file-size {
            color: map-get($colours, grey-3);
          }
        }

        .file-kept-label {
          color: map-get($colours, grey-5);
          margin-left: 1.2rem;
        }
      }

      .checkboxes {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-template-areas:
          "transcript audio"
          "link link";
        row-gap: 2.4rem;
        column-gap: 1.6rem;
        margin-top: 1.6rem;

        .field-checkbox {
          justify-self: start;

          label {
            @include callout;
            margin-left: 0.8rem;
            margin-top: 0.5rem;

            .file-size {
              color: map-get($colours, grey-3);
            }

            .email {
              color: map-get($colours, grey-5);
            }
          }
        }

        .transcript {
          grid-area: transcript;
        }

        .audio {
          grid-area: audio;
        }

        .link {
          grid-area: link;
        }
      }
    }

    .download-ready {
      .field-checkbox {
        display: flex;
        align-items: center;

        &:not(:first-child) {
          margin-top: 2.4rem;
        }
      }
    }

    .download-done {
      margin: 4.8rem 0;

      .download-file-label {
        &:not(:first-child) {
          margin-top: 2.4rem;
        }
      }
    }

    .download-file-label {
      margin-left: 1rem;
      display: flex;
      align-items: center;
      // grid-template-areas:
      //   "icon name"
      //   "icon size";
      grid-template-areas: "icon name";
      column-gap: 0.8rem;
      grid-template-columns: auto 1fr;

      .file-icon {
        grid-area: icon;
      }

      .file-name {
        grid-area: name;
        justify-self: start;
        @include body-1;
        color: map-get($colours, grey-6);
      }

      .file-size {
        grid-area: size;
        justify-self: start;
        @include body-2;
        color: map-get($colours, grey-4);
      }
    }
  }

  .p-dialog-footer {
    display: flex;
    flex-direction: row;
    // height: calc(4.8rem + #{$vertical-padding});
    padding: 0 4rem 4rem;

    .end-session-buttons {
      display: flex;
      flex-direction: row;
      height: 5.6rem;
      gap: 2.4rem;
    }

    .session-ended-buttons {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;

      > * {
        margin: 0;
      }
    }
  }

  header {
    @include h2-accent;
    display: flex;
    flex-direction: column;
    align-items: center;

    img {
      height: 8.8rem;
      width: 8.8rem;
      margin-bottom: 1.6rem;
    }
  }

  .content {
    @include section-sub-header;
  }

  .filename-disclaimer {
    display: flex;
    font-size: 1.4rem;
    color: map-get($colours, red);
    margin-top: 1.2rem;

    span {
      margin-left: 0.8rem;
    }
  }
}
</style>
